import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

/**
 * Shell Registry — single source of truth for every selectable UI layer.
 *
 * Adding a new shell:
 *   1. Create `src/shells/<id>/Shell.tsx` exporting `XxxShell`.
 *   2. Append a manifest entry below.
 * That's it — IndexRouter and the in-app switchers are registry-driven and
 * will pick the new shell up automatically.
 *
 * AI agents prototyping a competitor mimic should set `mimics: '<Product>'`
 * and `status: 'experimental'` so the entry is visibly not for production use.
 */

export type ShellStatus = 'stable' | 'experimental' | 'draft';

export interface LocalizedString {
  en: string;
  nl: string;
}

export interface ShellManifest {
  /** Stable id — written to localStorage and used in URLs (`?shell=<id>`). */
  id: string;
  /** Display name shown in switchers. Self-contained — no i18n.json entry required. */
  name: LocalizedString;
  /** Short tagline shown under the name. Optional. */
  description?: LocalizedString;
  /** Competitor or product this shell intentionally borrows from. */
  mimics?: string;
  /** Lifecycle stage. Experimental shells may be hidden from non-expert switchers later. */
  status: ShellStatus;
  /**
   * Lazy-loaded shell component.
   * `null` means "use the classic Index page" — reserved for the `clando` entry.
   */
  // ComponentType<any> instead of <unknown> because lazy() of a component with
  // its own props won't satisfy ComponentType<unknown>.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: LazyExoticComponent<ComponentType<any>> | null;
}

// Internal: keeps literal `id` types so `UIShell` can be derived below.
// The exported `SHELLS` widens to `ShellManifest[]` so iteration sees the full
// optional shape (otherwise `mimics`/`description` only exist on entries that
// happen to define them).
const _SHELLS = [
  {
    id: 'clando',
    name: { en: 'Classic Clando', nl: 'Klassiek Clando' },
    description: {
      en: 'The original tab-based household UI.',
      nl: 'De originele tab-gestuurde huishoud-UI.',
    },
    status: 'stable',
    Component: null,
  },
  {
    id: 'clando-next',
    name: { en: 'New UI', nl: 'Nieuwe UI' },
    description: {
      en: 'Calmer sidebar layout with a focused Today surface.',
      nl: 'Rustiger sidebar-lay-out met focus op Vandaag.',
    },
    status: 'experimental',
    Component: lazy(() =>
      import('./clando-next/ClandoNextShell').then((m) => ({ default: m.ClandoNextShell }))
    ),
  },
  {
    id: 'msto',
    name: { en: 'Power sidebar', nl: 'Power-sidebar' },
    description: {
      en: 'Sidebar, smart lists and detail pane for focused task work.',
      nl: 'Sidebar, slimme lijsten en detailpaneel voor gefocust takenwerk.',
    },
    mimics: 'Microsoft To Do',
    status: 'experimental',
    Component: lazy(() =>
      import('./msto/MstoShell').then((m) => ({ default: m.MstoShell }))
    ),
  },
  {
    id: 'rtm',
    name: { en: 'Dense lists', nl: 'Compacte lijsten' },
    description: {
      en: 'High-density task lists with tag navigation and quick scanning.',
      nl: 'Compacte takenlijsten met tagnavigatie en snel overzicht.',
    },
    mimics: 'Remember The Milk',
    status: 'experimental',
    Component: lazy(() =>
      import('./rtm/RtmShell').then((m) => ({ default: m.RtmShell }))
    ),
  },
  {
    id: 'bring',
    name: { en: 'Grocery tiles', nl: 'Boodschappen-tegels' },
    description: {
      en: 'Visual tile-based grocery list with a tap-to-re-add pantry catalog.',
      nl: 'Visuele boodschappenlijst met tegels en een tik-om-toe-te-voegen catalogus.',
    },
    mimics: 'Bring!',
    status: 'experimental',
    Component: lazy(() =>
      import('./bring/BringShell').then((m) => ({ default: m.BringShell }))
    ),
  },
  {
    id: 'ticktick',
    name: { en: 'Today & matrix', nl: 'Vandaag & matrix' },
    description: {
      en: 'Focused today list plus an Eisenhower priority matrix.',
      nl: 'Gefocuste vandaag-lijst plus een Eisenhower-prioriteitenmatrix.',
    },
    mimics: 'TickTick',
    status: 'experimental',
    Component: lazy(() =>
      import('./ticktick/TickTickShell').then((m) => ({ default: m.TickTickShell }))
    ),
  },
  {
    id: 'todoist',
    name: { en: 'Quick-add lists', nl: 'Snel-toevoegen lijsten' },
    description: {
      en: 'Natural-language quick-add with Inbox / Today / Upcoming views.',
      nl: 'Natuurlijke-taal toevoegen met Inbox / Vandaag / Aankomend.',
    },
    mimics: 'Todoist',
    status: 'experimental',
    Component: lazy(() =>
      import('./todoist/TodoistShell').then((m) => ({ default: m.TodoistShell }))
    ),
  },
  {
    id: 'cozi',
    name: { en: 'Family week', nl: 'Gezinsweek' },
    description: {
      en: 'Colour-coded family week view with per-member filtering.',
      nl: 'Kleurgecodeerde gezinsweek met filteren per gezinslid.',
    },
    mimics: 'Cozi',
    status: 'experimental',
    Component: lazy(() =>
      import('./cozi/CoziShell').then((m) => ({ default: m.CoziShell }))
    ),
  },
  {
    id: 'freelancer',
    name: { en: 'Freelancer', nl: 'Freelancer' },
    description: {
      en: 'Hours, projects and invoicing surfaces for solo work.',
      nl: 'Uren, projecten en facturen voor solo werk.',
    },
    status: 'experimental',
    Component: lazy(() =>
      import('./freelancer/FreelancerShell').then((m) => ({ default: m.FreelancerShell }))
    ),
  },
  {
    id: 'valuetime',
    name: { en: 'Time cockpit', nl: 'Tijdcockpit' },
    description: {
      en: 'Time-tracking-led work cockpit for projects and invoices.',
      nl: 'Tijdsregistratie-cockpit voor projecten en facturen.',
    },
    mimics: 'ValueTime',
    status: 'experimental',
    Component: lazy(() =>
      import('./valuetime/ValuetimeShell').then((m) => ({ default: m.ValuetimeShell }))
    ),
  },
  {
    id: 'garden',
    name: { en: 'Garden', nl: 'Tuin' },
    description: {
      en: 'Vertical face for landscaping & garden-maintenance teams (lens over tasks, tags & clients).',
      nl: 'Verticale schil voor tuin- en onderhoudsploegen (lens over taken, tags & klanten).',
    },
    status: 'experimental',
    Component: lazy(() =>
      import('./garden/GardenShell').then((m) => ({ default: m.GardenShell }))
    ),
  },
] as const satisfies readonly ShellManifest[];

/** Union of every registered shell id, derived from the registry. */
export type UIShell = (typeof _SHELLS)[number]['id'];

/**
 * Public, widened view of the registry — use this when iterating.
 * `id` stays typed as `UIShell` so it can flow into setters without casts.
 */
export const SHELLS: ReadonlyArray<ShellManifest & { id: UIShell }> = _SHELLS;

/** Default shell when no preference is stored. */
export const DEFAULT_SHELL: UIShell = 'clando';

export function getShell(id: string): ShellManifest | undefined {
  return SHELLS.find((s) => s.id === id);
}

export function isValidShellId(id: string): id is UIShell {
  return SHELLS.some((s) => s.id === id);
}

/**
 * Pick the localized name from a manifest. Falls back to English if the
 * requested language isn't an exact match (we only ship en/nl right now).
 */
export function shellName(manifest: ShellManifest, language: string): string {
  return language?.toLowerCase().startsWith('nl') ? manifest.name.nl : manifest.name.en;
}

export function shellDescription(
  manifest: ShellManifest,
  language: string
): string | undefined {
  if (!manifest.description) return undefined;
  return language?.toLowerCase().startsWith('nl')
    ? manifest.description.nl
    : manifest.description.en;
}
